<script setup lang="ts">
import { SheetDefinition } from "@/types/sheet-definiation";
import { useImageInstance, useSpriteSheetUrl } from "@/use/common";
import { watch } from "vue";
import { onMounted, ref } from "vue";
import { Sex, Animations } from "@/enums/characterGeneratorEnums";
/**
 * 为什么用Canvas，不用Background属性
 * - 因为单个Definiation中可能包含多图层，Canvas性能更高
 */

const props = withDefaults(
  defineProps<{
    sex: Sex;
    definition: SheetDefinition;
    animation?: Animations;
    variantIdx?: number;
  }>(),
  {
    animation: Animations.Walk,
    sex: Sex.Male,
    variantIdx: 0,
  }
);

const FRAME_SIZE = 64;

const canvas = ref<HTMLCanvasElement>();

onMounted(() => {
  const ctx: CanvasRenderingContext2D = canvas.value!.getContext("2d")!;
  buildLayers(ctx, props.definition, props.sex);

  watch([() => props.animation, () => props.sex], () => {
    if (!canvas.value) return;
    const ctx: CanvasRenderingContext2D = canvas.value!.getContext("2d")!;
    buildLayers(ctx, props.definition, props.sex);
  });
});

const buildLayers = (
  ctx: CanvasRenderingContext2D,
  definition: SheetDefinition,
  sex: Sex
) => {
  // 1. 根据选中的sex，获取根据图层排序后的图片路径
  const urls: string[] = getSortedUrls(definition, sex);
  // 2. 下载图片，并绘制到canvas上
  urls.forEach((url) => {
    const image = useImageInstance();
    image.src = useSpriteSheetUrl(
      url,
      props.animation,
      definition.variants[props.variantIdx]
    )
      .replace("${head}", "male")
      .replace("${expression}", "default");
    image.onload = () => {
      const column = definition.preview_column ?? 0;
      const row = definition.preview_row ?? 2;
      const preview_x_offset = definition.preview_x_offset ?? 0;
      const preview_y_offset = definition.preview_y_offset ?? 0;

      ctx.drawImage(
        image,
        column * FRAME_SIZE + preview_x_offset,
        row * FRAME_SIZE + preview_y_offset,
        FRAME_SIZE,
        FRAME_SIZE,
        0,
        0,
        FRAME_SIZE,
        FRAME_SIZE
      );
    };
  });
};

const getSortedUrls = (definition: SheetDefinition, sex: Sex): string[] => {
  return Object.keys(definition)
    .filter((keys) => keys.includes("layer_"))
    .filter((layer_key) => sex in definition[layer_key])
    .sort((a, b) => {
      const a_layer = definition[a];
      const b_layer = definition[b];
      return a_layer.zPos - b_layer.zPos;
    })
    .map((layer_key) => {
      const layer = definition[layer_key];
      return layer[sex];
    });
};
</script>
<template>
  <canvas ref="canvas" :width="FRAME_SIZE" :height="FRAME_SIZE" />
</template>
